import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface EnrichedFavorite {
  id: string;
  user_id: string;
  target_id: string;
  target_type: string;
  created_at: string;
  target_name: string;
  target_icon?: string;
  target_scope_type?: string;
  target_project_id?: string | null;
}

export const useFavorites = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['favorites'],
    enabled: !!user,
    queryFn: async (): Promise<EnrichedFavorite[]> => {
      const { data, error } = await supabase
        .from('user_favorites')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const favs = data ?? [];
      if (favs.length === 0) return [];

      // Group by type
      const byType: Record<string, string[]> = {};
      for (const f of favs) {
        (byType[f.target_type] ??= []).push(f.target_id);
      }

      const nameMap: Record<string, { name: string; icon?: string; scope_type?: string; project_id?: string | null }> = {};

      const resolvers: Promise<any>[] = [];

      if (byType['project']?.length) {
        resolvers.push(
          Promise.resolve(supabase.from('projects').select('id, name, icon').in('id', byType['project']))
            .then(({ data: d }) => { d?.forEach(r => { nameMap[r.id] = { name: r.name, icon: r.icon }; }); })
        );
      }
      if (byType['agent']?.length) {
        resolvers.push(
          Promise.resolve(supabase.from('agents').select('id, name, scope_type, project_id').in('id', byType['agent']))
            .then(({ data: d }) => { d?.forEach(r => { nameMap[r.id] = { name: r.name, scope_type: r.scope_type, project_id: r.project_id }; }); })
        );
      }
      if (byType['prompt']?.length) {
        resolvers.push(
          Promise.resolve(supabase.from('prompts').select('id, name').in('id', byType['prompt']))
            .then(({ data: d }) => { d?.forEach(r => { nameMap[r.id] = { name: r.name }; }); })
        );
      }
      if (byType['tool']?.length) {
        resolvers.push(
          Promise.resolve(supabase.from('tools').select('id, name').in('id', byType['tool']))
            .then(({ data: d }) => { d?.forEach(r => { nameMap[r.id] = { name: r.name }; }); })
        );
      }

      await Promise.all(resolvers);

      return favs.map(f => ({
        ...f,
        target_name: nameMap[f.target_id]?.name ?? f.target_id.slice(0, 8) + '…',
        target_icon: nameMap[f.target_id]?.icon,
        target_scope_type: nameMap[f.target_id]?.scope_type,
        target_project_id: nameMap[f.target_id]?.project_id,
      }));
    },
  });
};

export const useIsFavorite = (targetId: string) => {
  const { data: favorites } = useFavorites();
  return favorites?.some(f => f.target_id === targetId) ?? false;
};

export const useToggleFavorite = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ targetId, targetType }: { targetId: string; targetType: string }) => {
      // Check if already favorited
      const { data: existing } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', user!.id)
        .eq('target_id', targetId)
        .eq('target_type', targetType)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('id', existing.id);
        if (error) throw error;
        return { action: 'removed' as const };
      } else {
        const { error } = await supabase
          .from('user_favorites')
          .insert({ user_id: user!.id, target_id: targetId, target_type: targetType });
        if (error) throw error;
        return { action: 'added' as const };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
};
